import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Icons } from "../components/ui/icons";
import { Alert, AlertDescription } from "../components/ui/alert";
import { requestOtp, verifyOtp } from "../lib/api";
import { getBackendErrorMessage } from "../lib/utils";

export default function Login() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const navigate = useNavigate();
  const location = useLocation();

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      setLoading(false);
      return;
    }

    try {
      await requestOtp(email);
      setOtpSent(true);
      // Focus first OTP input after OTP is sent
      setTimeout(() => {
        otpInputRefs.current[0]?.focus();
      }, 100);
    } catch (err: unknown) {
      setError(getBackendErrorMessage(err, "Failed to send OTP"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const otpString = otp.join("");
    if (otpString.length !== 6) {
      setError("Please enter a valid 6-digit OTP");
      setLoading(false);
      return;
    }

    try {
      const response = await verifyOtp(email, otpString);

      // Store details on cookie
      document.cookie = `accessToken=${response.accessToken}; path=/; max-age=86400; SameSite=Strict`;
      document.cookie = `user=${encodeURIComponent(JSON.stringify(response.user))}; path=/; max-age=86400; SameSite=Strict`;

      // ProtectedRoute passes state={{ from: location }}; use it to redirect back after login
      const from = (location.state as { from?: { pathname: string; search?: string; hash?: string } })?.from;
      if (from?.pathname) {
        navigate(from, { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    } catch (err: unknown) {
      setError(getBackendErrorMessage(err, "Failed to verify OTP"));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setOtpSent(false);
    setOtp(Array(6).fill(""));
    setError(null);
  };

  const handleOtpChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, "");
    if (digit.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);
    setError(null);

    // Auto-focus next input if digit entered
    if (digit && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    // Handle backspace
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "");
    if (pastedData.length === 6) {
      const newOtp = pastedData.split("").slice(0, 6);
      setOtp(newOtp);
      otpInputRefs.current[5]?.focus();
    }
  };

  // Focus first OTP input when OTP sent
  useEffect(() => {
    if (otpSent) {
      setTimeout(() => {
        otpInputRefs.current[0]?.focus();
      }, 100);
    }
  }, [otpSent]);

  return (
    <div className="flex min-h-screen bg-gray-50/50">
      <div className="flex flex-1 flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <div className="mb-8 flex justify-center">
            <img
              src="/images/logo.png"
              alt="Logo"
              className="h-16 w-auto object-contain"
            />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-bold font-jakarta tracking-tight text-gray-900">
              Welcome back
            </h1>
            <p className="text-[14px] text-gray-600">
              Sign in to your account to continue
            </p>
          </div>

          <div className="mt-8">
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="mt-6">
              {!otpSent ? (
                <form onSubmit={handleSendOtp} className="space-y-4">
                  <div className="space-y-2">
                    <label
                      htmlFor="email"
                      className="text-[14px] font-medium text-gray-900"
                    >
                      Email address
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-10 text-[14px] bg-gray-50/50"
                      placeholder="Enter your email"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-10 text-[14px]"
                  >
                    {loading ? (
                      <>
                        <Icons.Spinner className="mr-2 h-4 w-4 animate-spin" />
                        Sending OTP...
                      </>
                    ) : (
                      "Send OTP"
                    )}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleVerifyOtp} className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-[14px] text-gray-600">
                      We've sent a 6-digit OTP to your email
                    </p>
                    <p className="text-[14px] font-medium text-gray-900">
                      {email}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[14px] font-medium text-gray-900">
                      Enter OTP
                    </label>
                    <div className="flex gap-2 justify-center">
                      {otp.map((digit, index) => (
                        <Input
                          key={index}
                          ref={(el) => {
                            otpInputRefs.current[index] = el;
                          }}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) =>
                            handleOtpChange(index, e.target.value)
                          }
                          onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          onPaste={handleOtpPaste}
                          className="h-12 w-12 text-center text-lg font-semibold bg-gray-50/50 focus:ring-2 focus:ring-primary-500"
                          autoFocus={index === 0 && otpSent}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleBack}
                      disabled={loading}
                      className="flex-1 h-10 text-[14px]"
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading || otp.join("").length !== 6}
                      className="flex-1 h-10 text-[14px]"
                    >
                      {loading ? (
                        <>
                          <Icons.Spinner className="mr-2 h-4 w-4 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        "Verify OTP"
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="relative hidden w-0 flex-1 lg:block">
        <div className="absolute inset-3">
          <img
            className="h-full w-full object-cover rounded-2xl"
            src="/images/IMG_2650.JPG"
            alt="Abstract background"
          />
        </div>
      </div>
    </div>
  );
}
